
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import {
  UserPlus,
  X,
  Search,
  Edit2,
  Trash2,
  Mail,
  Phone,
  Shield,
  Building2,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Upload,
  Image as ImageIcon,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Check,
  Key,
  Package,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';
export default function UserManagement({ setCurrentPage }) {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPageNum] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropType, setCropType] = useState(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropSettings, setCropSettings] = useState({ zoom: 1, rotation: 0 });
  const canvasRef = useRef(null);
  const itemsPerPage = 6;

  // License Key Modal States
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [clients, setClients] = useState([]);
  const [usersByClient, setUsersByClient] = useState({});
  const [licenseSearchTerm, setLicenseSearchTerm] = useState('');
  const [licenseCurrentPage, setLicenseCurrentPage] = useState(1);
  const [viewingServiceDetails, setViewingServiceDetails] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [viewingClient, setViewingClient] = useState(false);
  const [loadingLicense, setLoadingLicense] = useState(false);
  const licenseItemsPerPage = 5;

  const getDepartmentName = (id) => {
    const dept = {
      1: 'IT Department',
      2: 'HR Department',
      3: 'Finance Department',
      10: 'Operations Department'
    };
    return dept[id] || 'Unknown';
  };

  const getUserLevelName = (id) => {
    const levels = {
      1: 'Super Admin',
      2: 'Admin',
      3: 'User',
      5: 'Test User'
    };
    return levels[id] || 'Unknown';
  };

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    positionId: '',
    departmentId: '',
    userLevelId: '',
    isActive: 1,
    contactNo: '',
    licenseKey: '',
    keyExpired: null,  // ← ADD THIS
    profile: null,
    authorized: '',

    digitalSignFile: null
  });

  useEffect(() => {
    fetchUsers();
  }, []);



  useEffect(() => {
    fetch(`${API_BASE_URL}api/Department`)
      .then(res => res.json())
      .then(data => {
        // optional: filter active lang
        const activeDepartments = data.filter(d => d.isActive === 1);
        setDepartments(activeDepartments);
      })
      .catch(err => {
        console.error("Error fetching departments:", err);
      });
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/Users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        alert('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error fetching users');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch License Keys from Firebase
  const fetchLicenseCards = async () => {
    setLoadingLicense(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/LicenseKey`, {
        headers: { 'accept': 'text/plain' }
      });

      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const apiLicenses = await response.json();
      const apiKeys = apiLicenses
        .filter(item => item.isActive)
        .map(item => item.licenseKeyValue);

      const ref = collection(db, "services");
      const snapshot = await getDocs(ref);

      const filtered = snapshot.docs
        .filter(doc => apiKeys.includes(doc.id))
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          hasLicense: true,
        }));

      setFilteredServices(filtered);
    } catch (err) {
      console.error("Error loading license cards:", err);
      alert('Failed to load license keys. Please check if server is running.');
      setFilteredServices([]);
    } finally {
      setLoadingLicense(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewLicense = async (service) => {
    setSelectedLicense(service);
    setViewingServiceDetails(true);
    setClients([]);
    setUsersByClient({});
    setLoadingLicense(true);

    try {
      const clientsRef = collection(db, "services", service.id, "clients");
      const clientSnap = await getDocs(clientsRef);
      const clientList = clientSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientList);

      const usersMap = {};
      for (const client of clientList) {
        const usersRef = collection(db, "services", service.id, "clients", client.id, "users");
        const userSnap = await getDocs(usersRef);
        usersMap[client.id] = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      setUsersByClient(usersMap);
    } catch (err) {
      console.error("Error loading clients and users:", err);
    } finally {
      setLoadingLicense(false);
    }
  };

  const handleSelectLicenseUser = async (user) => {
    if (user.isTaken) {
      alert('This license is already taken!');
      return;
    }

    try {
      // Mark as taken in Firebase
      const userRef = doc(db, "services", selectedLicense.id, "clients", selectedClientId, "users", user.id);
      await updateDoc(userRef, { isTaken: true });

      // Update form data
      const expirationDate = user.subscriptionEnd?.seconds
        ? new Date(user.subscriptionEnd.seconds * 1000).toISOString().split('T')[0]
        : null;

      setFormData({
        ...formData,
        licenseKey: user.licenseKey || selectedLicense.id,
        keyExpired: expirationDate  // ← ADD THIS
      });
      // Close modal
      setShowLicenseModal(false);
      setViewingServiceDetails(false);
      setViewingClient(false);
      setSelectedClientId(null);
      setSelectedLicense(null);

      alert('License key selected successfully!');
    } catch (error) {
      console.error("Failed to mark license as taken:", error);
      alert('Failed to select license. Please try again.');
    }
  };

  const getAllUsers = () => {
    const allUsers = [];
    clients.forEach(client => {
      const users = usersByClient[client.id] || [];
      users.forEach(user => {
        allUsers.push({
          ...user,
          clientName: client.name,
          clientId: client.id
        });
      });
    });
    return allUsers;
  };

  const filteredLicenseUsers = getAllUsers().filter(user => {
    const searchLower = licenseSearchTerm.toLowerCase();
    return (
      user.licenseKey?.toLowerCase().includes(searchLower) ||
      user.userCode?.toLowerCase().includes(searchLower) ||
      user.clientName?.toLowerCase().includes(searchLower)
    );
  });

  const totalLicensePages = Math.ceil(filteredLicenseUsers.length / licenseItemsPerPage);
  const paginatedLicenseUsers = filteredLicenseUsers.slice(
    (licenseCurrentPage - 1) * licenseItemsPerPage,
    licenseCurrentPage * licenseItemsPerPage
  );

  const takenCount = getAllUsers().filter(u => u.isTaken).length;
  const availableCount = getAllUsers().filter(u => !u.isTaken).length;

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageToCrop(event.target.result);
        setCropType(name === 'profile' ? 'profile' : 'signature');
        setShowCropModal(true);
        setCropSettings({ zoom: 1, rotation: 0 });
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleCropComplete = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Convert canvas to DataURL (for preview)
    const croppedImageDataURL = canvas.toDataURL("image/png");

    canvas.toBlob((blob) => {
      if (!blob) return;

      const croppedBlob = blob;

      // Set preview for signature crop
      if (cropType === "signature") {
        setCroppedSignature(croppedImageDataURL);
        setFormData({
          ...formData,
          digitalSignFile: croppedBlob,
        });
      }

      // Save cropped file to formData
      const file = new File([croppedBlob], `${cropType}.png`, {
        type: "image/png",
      });

      setFormData({
        ...formData,
        [cropType]: file,
      });

      // Close modal & reset
      setShowCropModal(false);
      setImageToCrop(null);
      setCropType(null);
    }, "image/png");
  };

  useEffect(() => {
    if (imageToCrop && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const size = cropType === 'profile' ? 300 : 400;
        canvas.width = size;
        canvas.height = cropType === 'profile' ? size : size / 2;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((cropSettings.rotation * Math.PI) / 180);
        ctx.scale(cropSettings.zoom, cropSettings.zoom);

        const drawWidth = img.width;
        const drawHeight = img.height;

        ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        ctx.restore();
      };

      img.src = imageToCrop;
    }
  }, [imageToCrop, cropSettings, cropType]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.username || !formData.password || !formData.firstName ||
      !formData.lastName || !formData.email || !formData.contactNo ||
      !formData.positionId || !formData.departmentId || !formData.userLevelId ||
      !formData.Authorized) {

      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in all required fields',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    setIsLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append('Username', formData.username);
    formDataToSend.append('Password', formData.password);
    formDataToSend.append('FirstName', formData.firstName);
    formDataToSend.append('LastName', formData.lastName);
    formDataToSend.append('Email', formData.email);
    formDataToSend.append('PositionId', formData.positionId);
    formDataToSend.append('DepartmentId', formData.departmentId);
    formDataToSend.append('UserLevelId', formData.userLevelId);
    formDataToSend.append('IsActive', formData.isActive);
    formDataToSend.append('ContactNo', formData.contactNo);
    formDataToSend.append('LicenseKey', formData.licenseKey);
    formDataToSend.append('Authorized', formData.Authorized); // ← DAGDAG MO TO!

    if (formData.profile) {
      formDataToSend.append('Profile', formData.profile);
    }
    if (formData.digitalSignFile) {
      formDataToSend.append('DigitalSignFile', formData.digitalSignFile);
    }
    if (formData.keyExpired) {
      formDataToSend.append('Key_expired', formData.keyExpired);
    }

    try {
      let response;
      if (editMode) {
        response = await fetch(`${API_BASE_URL}api/Users/${editingUserId}`, {
          method: 'PUT',
          body: formDataToSend
        });
      } else {
        response = await fetch(`${API_BASE_URL}api/Users`, {
          method: 'POST',
          body: formDataToSend
        });
      }

      if (response.ok) {
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: editMode ? 'User updated successfully!' : 'User created successfully!',
          confirmButtonColor: '#10b981',
          timer: 2000,
          showConfirmButton: true
        });

        setShowModal(false);
        setEditMode(false);
        setEditingUserId(null);
        fetchUsers();
        resetForm();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to save user. Please try again.',
          confirmButtonColor: '#ef4444'
        });
      }
    } catch (error) {
      console.error('Error saving user:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while saving the user.',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const [userLevels, setUserLevels] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/UserLevels`)
      .then(res => res.json())
      .then(data => setUserLevels(data))
      .catch(err => console.error("Failed to load user levels:", err));
  }, []);

  const handleEdit = async (user) => {
    setEditMode(true);
    setEditingUserId(user.id);

    // Fetch full user details to get licenseKey
    try {
      const response = await fetch(`${API_BASE_URL}api/Users/${user.id}`);
      if (response.ok) {
        const userData = await response.json();
        setFormData({
          username: userData.username || '',
          password: userData.password || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          positionId: userData.positionId || '',
          departmentId: userData.departmentId || '',
          userLevelId: userData.userLevelId || '',
          isActive: userData.isActive,
          contactNo: userData.contactNo || '',
          licenseKey: userData.licenseKey || '',
          keyExpired: userData.key_expired ? new Date(userData.key_expired).toISOString().split('T')[0] : null,
          Authorized: userData.authorized || '', // ← DAGDAG MO TO!
          profile: null,
          digitalSignFile: null
        });
      }
    } catch (error) {
      console.error('Error fetching user details:', error);

      Swal.fire({
        icon: 'error',
        title: 'Oops!',
        text: 'Failed to load user details. Using basic data.',
        confirmButtonColor: '#f59e0b',
        timer: 2000
      });

      // Fallback to basic user data
      setFormData({
        username: user.username || '',
        password: user.password || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        positionId: user.positionId || '',
        departmentId: user.departmentId || '',
        userLevelId: user.userLevelId || '',
        isActive: user.isActive,
        contactNo: user.contactNo || '',
        licenseKey: user.licenseKey || '',
        keyExpired: user.key_expired ? new Date(user.key_expired).toISOString().split('T')[0] : null,
        Authorized: user.authorized || '', // ← DAGDAG DIN DITO!
        profile: null,
        digitalSignFile: null
      });
    }

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/Users/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('User deleted successfully!');
        fetchUsers();
      } else {
        alert('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      positionId: '',
      departmentId: '',
      userLevelId: '',
      isActive: 1,
      contactNo: '',
      licenseKey: '',
      profile: null,
      digitalSignFile: null
    });
  };

  const filteredUsers = users.filter(user =>
    (user.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  const [croppedSignature, setCroppedSignature] = useState(null);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">User Management</h1>
          <p className="text-slate-600">Manage system users and permissions</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-3 rounded-xl mr-12 hover:from-blue-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <UserPlus className="w-5 h-5" />
          Create User
        </button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {isLoading ? (
          <div className="col-span-full text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        ) : paginatedUsers.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-600">No users found</p>
          </div>
        ) : (
          paginatedUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-white/50 hover:-translate-y-1"
            >
              <div className="bg-gradient-to-r from-blue-600 to-blue-600 p-6 text-white">
                <div className="flex items-center justify-between mb-3">
                  {user.profileBase64 ? (
                    <img
                      src={`${API_BASE_URL}api/Users/${user.id}/profile`}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold backdrop-blur-sm"
                    style={{ display: user.profileBase64 ? 'none' : 'flex' }}
                  >
                    {(user.firstName?.[0] || '?')}{(user.lastName?.[0] || '?')}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="p-2 bg-white/20 rounded-lg hover:bg-red-500 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="text-xl font-bold">{user.firstName} {user.lastName}</h3>
                <p className="text-blue-100 text-sm">@{user.username}</p>
              </div>

              <div className="p-6 space-y-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span className="text-sm truncate">{user.email}</span>
                </div>

                <div className="flex items-center gap-3 text-slate-700">
                  <Phone className="w-4 h-4 text-green-500" />
                  <span className="text-sm">{user.contactNo}</span>
                </div>

                <div className="flex items-center gap-3 text-slate-700">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">{user.positionId}</span>
                </div>

                <div className="flex items-center gap-3 text-slate-700">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">{getDepartmentName(user.departmentId)}</span>
                </div>

                <div className="flex items-center gap-3 text-slate-700">
                  <Shield className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-semibold">{getUserLevelName(user.userLevelId)}</span>
                </div>

                <div className="pt-3 border-t border-slate-200">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${user.isActive === 1
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                    }`}>
                    {user.isActive === 1 ? '● Active' : '● Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4">
          <button
            onClick={() => setCurrentPageNum(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="p-2 bg-white rounded-lg shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex gap-2">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPageNum(i + 1)}
                className={`w-10 h-10 rounded-lg font-semibold transition-all ${currentPage === i + 1
                  ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
                  }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPageNum(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="p-2 bg-white rounded-lg shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-center rounded-t-2xl z-10">
              <h2 className="text-2xl font-bold">{editMode ? 'Edit User' : 'Create New User'}</h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditMode(false);
                  setEditingUserId(null);
                  resetForm();
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-8">
              {/* Account Information Section */}
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b-2 border-blue-500 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Account Information
                </h3>
                <div>


                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Profile Picture Upload - Facebook Style with Preview */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-3">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-blue-500" />
                          Profile Picture
                        </div>
                      </label>

                      <div className="flex flex-col items-center gap-4">
                        {/* Circular Profile Preview */}
                        <div className="relative">
                          {imageToCrop && cropType === 'profile' ? (
                            <div className="w-40 h-40 rounded-full border-4 border-blue-500 overflow-hidden shadow-xl">
                              <img
                                src={imageToCrop}
                                alt="Profile Preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : formData.profile ? (
                            <div className="w-40 h-40 rounded-full border-4 border-blue-500 overflow-hidden shadow-xl">
                              <img
                                src={URL.createObjectURL(formData.profile)}
                                alt="Profile Preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : editMode && editingUserId ? (
                            <div className="w-40 h-40 rounded-full border-4 border-slate-300 overflow-hidden shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                              <img
                                src={`${API_BASE_URL}api/Users/${editingUserId}/profile`}
                                alt="Current Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="w-full h-full hidden items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
                                <ImageIcon className="w-16 h-16 text-blue-400" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-40 h-40 rounded-full border-4 border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center shadow-inner">
                              <ImageIcon className="w-16 h-16 text-slate-400" />
                            </div>
                          )}

                          {/* Camera Icon Button Overlay */}
                          <label className="absolute bottom-2 right-2 w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-all hover:scale-110 border-4 border-white">
                            <Upload className="w-5 h-5 text-white" />
                            <input
                              type="file"
                              name="profile"
                              onChange={handleInputChange}
                              accept="image/*"
                              className="hidden"
                            />
                          </label>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <label className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold cursor-pointer transition-all shadow-md hover:shadow-lg flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            {formData.profile ? 'Change Photo' : 'Upload Photo'}
                            <input
                              type="file"
                              name="profile"
                              onChange={handleInputChange}
                              accept="image/*"
                              className="hidden"
                            />
                          </label>

                          {formData.profile && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, profile: null })}
                              className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-all flex items-center gap-2"
                            >
                              <X className="w-4 h-4" />
                              Remove
                            </button>
                          )}
                        </div>

                        <p className="text-xs text-slate-500 text-center">
                          PNG, JPG up to 5MB
                        </p>
                      </div>
                    </div>

                    {/* Digital Signature Upload - Rectangle Preview */}
                    {/* Digital Signature Upload - Rectangle Preview */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-3">
                        <div className="flex items-center gap-2">
                          <Upload className="w-4 h-4 text-blue-500" />
                          Digital Signature
                        </div>
                      </label>

                      <div className="flex flex-col items-center gap-4">
                        {/* Signature Preview Box */}
                        <div className="w-full aspect-[2/1] border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden shadow-inner">

                          {/* 1. CROPPED SIGNATURE */}
                          {croppedSignature && cropType !== 'signature' ? (
                            <img
                              src={croppedSignature}
                              alt="Signature Preview"
                              className="w-full h-full object-contain p-4"
                            />

                            /* 2. IMAGE TO CROP (before crop) */
                          ) : imageToCrop && cropType === 'signature' ? (
                            <img
                              src={imageToCrop}
                              alt="Signature Preview"
                              className="w-full h-full object-contain p-4"
                            />

                            /* 3. UPLOADED RAW FILE (no crop yet) */
                          ) : formData.digitalSignFile ? (
                            <img
                              src={URL.createObjectURL(formData.digitalSignFile)}
                              alt="Signature Preview"
                              className="w-full h-full object-contain p-4"
                            />

                            /* 4. EXISTING SIGNATURE FROM DATABASE (edit mode) */
                          ) : editMode && editingUserId ? (
                            <>
                              <img
                                src={`${API_BASE_URL}api/Users/${editingUserId}/digitalsign`}
                                alt="Signature Preview"
                                className="w-full h-full object-contain p-4"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            </>

                            /* 5. EMPTY STATE */
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-slate-400">
                              <Upload className="w-12 h-12" />
                              <p className="text-sm font-medium">No signature uploaded</p>
                            </div>
                          )}
                        </div>


                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <label className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold cursor-pointer transition-all shadow-md hover:shadow-lg flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            {formData.digitalSignFile ? 'Change Signature' : 'Upload Signature'}
                            <input
                              type="file"
                              name="digitalSignFile"
                              onChange={handleInputChange}
                              accept="image/*"
                              className="hidden"
                            />
                          </label>

                          {formData.digitalSignFile && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, digitalSignFile: null })}
                              className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-all flex items-center gap-2"
                            >
                              <X className="w-4 h-4" />
                              Remove
                            </button>
                          )}
                        </div>

                        <p className="text-xs text-slate-500 text-center">
                          PNG, JPG up to 5MB
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Enter username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        maxLength={20}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12 transition-all"
                        placeholder="Enter password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-blue-500" />
                        License Key <span className="text-red-500">*</span>
                      </div>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="licenseKey"
                        value={formData.licenseKey}
                        onChange={handleInputChange}
                        maxLength={100}
                        className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder="Click 'Browse' to select from available licenses"
                        readOnly
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowLicenseModal(true);
                          fetchLicenseCards();
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-500 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-600 transition-all shadow-lg flex items-center gap-2"
                      >
                        <Eye className="w-5 h-5" />
                        Browse
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="isActive"
                      value={formData.isActive}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white"
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Personal Information Section */}
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b-2 border-blue-500 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      maxLength={50}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Enter first name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      maxLength={50}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Enter last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Contact Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contactNo"
                      value={formData.contactNo}
                      onChange={handleInputChange}
                      maxLength={50}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="09123456789"
                    />
                  </div>
                </div>
              </div>

              {/* Work Information Section */}
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 pb-2 border-b-2 border-blue-500 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  Work Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Position <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="positionId"
                      value={formData.positionId}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="e.g., Software Engineer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="departmentId"
                      value={formData.departmentId}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dep => (
                        <option key={dep.id} value={dep.id}>
                          {dep.departmentName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      User Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="userLevelId"
                      value={formData.userLevelId}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white"
                    >
                      <option value="">Select User Level</option>
                      {userLevels.map((lvl) => (
                        <option key={lvl.id} value={lvl.id}>
                          {lvl.userLevelName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Authorized <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="Authorized"
                      value={formData.Authorized}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white"
                    >
                      <option value="">Select Authorization</option>
                      <option value="Administrator">Administrator</option>
                      <option value="Spareparts">Spareparts</option>
                      <option value="Marketing">Marketing</option>
                      <option value="ASSISTANT PLANT MANAGER">ASSISTANT PLANT MANAGER</option>
                      <option value="VP-FINANCE/ADMIN">VP-FINANCE/ADMIN</option>
                      <option value="PRESIDENT/CEO">PRESIDENT/CEO</option>
                      <option value="Gas man">Gas man</option>
                      <option value="Supervisor">Supervisor</option>
                      <option value="Purchasing">Purchasing</option>


                    </select>
                  </div>
                </div>
              </div>
              {/* Files Upload Section - IMPROVED */}


              {/* Action Buttons */}
              <div className="flex gap-4 pt-6 border-t-2 border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditMode(false);
                    setEditingUserId(null);
                    resetForm();
                  }}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all font-semibold"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {editMode ? 'Updating...' : 'Creating...'}
                    </div>
                  ) : (
                    editMode ? 'Update User' : 'Create User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )
      }
      {/* License Key Browse Modal */}
      {
        showLicenseModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[70]">
            <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              {!viewingServiceDetails ? (
                <>
                  <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-8">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Package size={32} className="text-white" />
                        </div>
                        <div>
                          <h2 className="text-3xl font-bold mb-2">Select License Key</h2>
                          <p className="text-blue-100">Choose from available license keys</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowLicenseModal(false);
                          setViewingServiceDetails(false);
                          setSelectedLicense(null);
                        }}
                        className="p-2 hover:bg-white/20 rounded-lg transition-all"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  </div>

                  {/* License Cards Grid */}
                  <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {loadingLicense ? (
                      <div className="text-center py-12">
                        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading license keys...</p>
                      </div>
                    ) : filteredServices.length === 0 ? (
                      <div className="text-center py-20">
                        <Package size={56} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg font-medium">No license keys found</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredServices.map(service => (
                          <div
                            key={service.id}
                            className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-blue-200 hover:border-blue-400"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-600 flex items-center justify-center shadow-lg">
                                <Key size={24} className="text-white" />
                              </div>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                ● Active
                              </span>
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-2">{service.name || "License Key"}</h3>
                            <p className="text-sm text-gray-600 font-mono mb-4 break-all">{service.id}</p>

                            <div className="flex items-center gap-2 text-sm text-gray-700 mb-6">
                              <Calendar size={16} className="text-blue-500" />
                              <span>{formatDate(service.createdAt)}</span>
                            </div>

                            <button
                              onClick={() => handleViewLicense(service)}
                              className="w-full bg-gradient-to-r from-blue-600 to-blue-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-700 transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                              <Eye size={18} />
                              Select License
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : !viewingClient ? (
                <>
                  {/* Service Details View - Client Selection */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => {
                            setViewingServiceDetails(false);
                            setSelectedLicense(null);
                            setClients([]);
                          }}
                          className="p-2 hover:bg-white/20 rounded-lg transition-all"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <div>
                          <h2 className="text-3xl font-bold mb-2">{selectedLicense?.name || "License Key"}</h2>
                          <p className="text-blue-100 font-mono text-sm">{selectedLicense?.id}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowLicenseModal(false);
                          setViewingServiceDetails(false);
                          setSelectedLicense(null);
                        }}
                        className="p-2 hover:bg-white/20 rounded-lg transition-all"
                      >
                        <X size={24} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 size={20} />
                          <span className="text-white/80 text-sm">Clients</span>
                        </div>
                        <p className="text-3xl font-bold">{clients.length}</p>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Users size={20} />
                          <span className="text-white/80 text-sm">Total Users</span>
                        </div>
                        <p className="text-3xl font-bold">{getAllUsers().length}</p>
                      </div>
                      <div className="bg-green-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle size={20} />
                          <span className="text-white/80 text-sm">Taken</span>
                        </div>
                        <p className="text-3xl font-bold">{takenCount}</p>
                      </div>
                      <div className="bg-yellow-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock size={20} />
                          <span className="text-white/80 text-sm">Available</span>
                        </div>
                        <p className="text-3xl font-bold">{availableCount}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-8 overflow-y-auto max-h-[calc(90vh-280px)]">
                    {loadingLicense ? (
                      <div className="text-center py-12">
                        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading clients...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {clients.map(client => {
                          const clientUsers = usersByClient[client.id] || [];
                          const clientTaken = clientUsers.filter(u => u.isTaken).length;

                          return (
                            <div
                              key={client.id}
                              onClick={() => {
                                setSelectedClientId(client.id);
                                setViewingClient(true);
                              }}
                              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200 hover:border-blue-400 cursor-pointer hover:shadow-xl transition-all"
                            >
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                                    <Building2 size={24} className="text-white" />
                                  </div>
                                  <div>
                                    <h3 className="text-xl font-bold text-gray-900">{client.name || "Client"}</h3>
                                    <p className="text-sm text-gray-600">Click to view users</p>
                                  </div>
                                </div>
                                <ChevronRight size={24} className="text-gray-400" />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white rounded-lg p-3 text-center">
                                  <p className="text-xs text-gray-600 mb-1">Total Users</p>
                                  <p className="text-lg font-bold text-blue-600">{clientUsers.length}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 text-center">
                                  <p className="text-xs text-gray-600 mb-1">Available</p>
                                  <p className="text-lg font-bold text-green-600">{clientUsers.length - clientTaken}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Client Users View - User Selection */}
                  <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-8">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => {
                            setViewingClient(false);
                            setSelectedClientId(null);
                            setLicenseSearchTerm('');
                            setLicenseCurrentPage(1);
                          }}
                          className="p-2 hover:bg-white/20 rounded-lg transition-all"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <div>
                          <h2 className="text-3xl font-bold mb-2">
                            {clients.find(c => c.id === selectedClientId)?.name || "Client Users"}
                          </h2>
                          <p className="text-blue-100 text-sm">Select an available license</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowLicenseModal(false);
                          setViewingServiceDetails(false);
                          setViewingClient(false);
                          setSelectedLicense(null);
                        }}
                        className="p-2 hover:bg-white/20 rounded-lg transition-all"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  </div>

                  <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {/* Search Bar */}
                    <div className="mb-6">
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          placeholder="Search by license key or user code..."
                          value={licenseSearchTerm}
                          onChange={(e) => {
                            setLicenseSearchTerm(e.target.value);
                            setLicenseCurrentPage(1);
                          }}
                          className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Users Table */}
                    <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-lg">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-100 border-b-2 border-gray-200">
                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">User Code</th>
                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">License Key</th>
                            <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">Expiredate</th>

                            <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">Status</th>
                            <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedLicenseUsers.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                No users found.
                              </td>
                            </tr>
                          ) : (
                            paginatedLicenseUsers
                              .filter(user => user.clientId === selectedClientId)
                              .map(user => (
                                <tr key={user.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                  <td className="px-6 py-4">
                                    <span className="font-mono text-sm font-semibold text-gray-900">{user.userCode || "N/A"}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="font-mono text-sm text-gray-700">{user.licenseKey || selectedLicense?.id}</span>
                                  </td>
                                  <td className="px-4 py-4">
                                    <input
                                      type="date"
                                      value={user.subscriptionEnd?.seconds
                                        ? new Date(user.subscriptionEnd.seconds * 1000).toISOString().slice(0, 10)
                                        : ''}
                                      onChange={(e) => e.stopPropagation()}
                                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm max-w-[150px]"
                                    />
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    {user.isTaken ? (
                                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                                        <XCircle size={14} />
                                        Taken
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                                        <CheckCircle size={14} />
                                        Available
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <button
                                      onClick={() => handleSelectLicenseUser(user)}
                                      disabled={user.isTaken}
                                      className={`px-4 py-2 rounded-lg font-semibold transition-all ${user.isTaken
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-blue-600 to-blue-600 text-white hover:from-blue-700 hover:to-blue-700 shadow-lg'
                                        }`}
                                    >
                                      {user.isTaken ? 'Unavailable' : 'Select'}
                                    </button>
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {totalLicensePages > 1 && (
                        <div className="flex justify-center items-center gap-2 p-6 border-t-2 border-gray-200 bg-gray-50">
                          <button
                            onClick={() => setLicenseCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={licenseCurrentPage === 1}
                            className={`p-2 rounded-lg font-semibold transition-all ${licenseCurrentPage === 1
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-blue-50 border-2 border-gray-300'
                              }`}
                          >
                            <ChevronLeft size={20} />
                          </button>

                          {[...Array(totalLicensePages)].map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setLicenseCurrentPage(i + 1)}
                              className={`min-w-[40px] px-4 py-2 rounded-lg font-semibold transition-all ${licenseCurrentPage === i + 1
                                ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 hover:bg-blue-50 border-2 border-gray-300'
                                }`}
                            >
                              {i + 1}
                            </button>
                          ))}

                          <button
                            onClick={() => setLicenseCurrentPage(prev => Math.min(totalLicensePages, prev + 1))}
                            disabled={licenseCurrentPage === totalLicensePages}
                            className={`p-2 rounded-lg font-semibold transition-all ${licenseCurrentPage === totalLicensePages
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-blue-50 border-2 border-gray-300'
                              }`}
                          >
                            <ChevronRight size={20} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      }
      {/* Image Crop Modal */}
      {
        showCropModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                  Crop {cropType === 'profile' ? 'Profile Picture' : 'Digital Signature'}
                </h2>
                <button
                  onClick={() => {
                    setShowCropModal(false);
                    setImageToCrop(null);
                    setCropType(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 max-h-[calc(95vh-140px)] overflow-y-auto">
                {/* Canvas Preview - Wide and Centered */}
                <div className="bg-slate-100 rounded-lg p-6 mb-6 flex items-center justify-center min-h-[400px]">
                  <canvas
                    ref={canvasRef}
                    className="max-w-full max-h-[500px] border-2 border-slate-300 rounded-lg shadow-lg bg-white"
                  />
                </div>

                {/* Crop Controls */}
                <div className="space-y-6">
                  {/* ZOOM */}
                  <div>
                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <ZoomIn className="w-4 h-4" />
                      Zoom: {cropSettings.zoom.toFixed(1)}x
                    </label>

                    <input
                      type="range"
                      min="0.1"
                      max="3"
                      step="0.1"
                      value={cropSettings.zoom}
                      onChange={(e) =>
                        setCropSettings({
                          ...cropSettings,
                          zoom: Number(e.target.value),
                        })
                      }
                      className="range-slider"
                    />
                  </div>

                  {/* ROTATION */}
                  <div>
                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                      <RotateCw className="w-4 h-4" />
                      Rotation: {cropSettings.rotation}°
                    </label>

                    <input
                      type="range"
                      min="0"
                      max="360"
                      step="1"
                      value={cropSettings.rotation}
                      onChange={(e) =>
                        setCropSettings({
                          ...cropSettings,
                          rotation: Number(e.target.value),
                        })
                      }
                      className="range-slider"
                    />
                  </div>

                  {/* Custom Styles */}
                  <style jsx>{`
    .range-slider {
      width: 100%;
      height: 6px;
      border-radius: 9999px;
      background: #e2e8f0;
      appearance: none;
      cursor: pointer;
    }

    .range-slider::-webkit-slider-thumb {
      appearance: none;
      height: 18px;
      width: 18px;
      background: #2563eb;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
      margin-top: -6px;
      cursor: pointer;
      transition: transform 0.15s ease;
    }

    .range-slider::-webkit-slider-thumb:hover {
      transform: scale(1.15);
    }

    .range-slider::-moz-range-thumb {
      height: 18px;
      width: 18px;
      background: #2563eb;
      border-radius: 50%;
      border: 2px solid white;
      cursor: pointer;
    }
  `}</style>
                </div>


                {/* Action Buttons */}
                <div className="flex gap-4 mt-6 pt-6 border-t-2 border-slate-200">
                  <button
                    onClick={() => {
                      setShowCropModal(false);
                      setImageToCrop(null);
                      setCropType(null);
                    }}
                    className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropComplete}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all font-semibold shadow-lg flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    Apply Crop
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}